import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { FiringProvider } from 'contexts/firing/context';
import { MachineContext } from 'contexts/machine.context';
import { addMilliseconds, addSeconds } from 'date-fns';
import { ResetPlateMode, USE_ABS_PITCH_BACKUP } from 'enums/machine.enums';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLatest from 'hooks/useLatestRef';
import { IVerboseComponent } from 'interfaces/i-components';
import { IIgnoreAutoFireProp } from 'interfaces/i-firing';
import {
  ISendBP,
  ISetPitchBP,
  ISetPitchTraining,
} from 'interfaces/i-pitch-aiming';
import { AimingHelper } from 'lib_ts/classes/aiming.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import {
  StaticVideoType,
  TroubleshootingPitchID,
} from 'lib_ts/enums/machine-msg.enum';
import {
  BuildPriority,
  DEFAULT_DELIVERY,
  PitcherHand,
} from 'lib_ts/enums/pitches.enums';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { AUTO_USE_TOP_SUGGESTION } from 'lib_ts/enums/videos.enums';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import {
  IAimTrainingRequest,
  IAimedMsMsgRequest,
  IAimedMsMsgResult,
  IAimingOutputs,
} from 'lib_ts/interfaces/i-pitch-aiming';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { IMachineStateMsg } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import {
  DEFAULT_PLATE,
  IPitch,
  IPlateLoc,
  ITrainingPitch,
} from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { clone, first, isNil } from 'lodash';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { PitchListsService } from 'services/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';
import useHittersStore from 'stores/use-hitters-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMiscStore from 'stores/use-misc-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { useVideosStore } from 'stores/use-videos-store';

const CONTEXT_NAME = 'AimingContext';

// aim w/ aimingTraj on TMS (if defined), fallback to aiming w/ shots and memoize the result in aimingTraj for next time
const PREFER_AIM_WITH_TRAJ_FOR_BP = true;

// ! deprecate this once aiming CI tests are implemented
// if true, always computes aimedWithShots and compares it to aimedWithTraj if possible
const COMPARE_AIM_WITH_TRAJ_AND_SHOTS_FOR_BP = true;

// upper bound on how long a send may mask the fire state, in case a send never settles
const SEND_TIMEOUT_SEC = 10;

// MARK: compareAiming
const compareAiming = (config: {
  aimedWithTraj: IAimingOutputs;
  aimedWithShots: IAimingOutputs;
}) => {
  const FN_NAME = 'compareAiming';

  if (!COMPARE_AIM_WITH_TRAJ_AND_SHOTS_FOR_BP) {
    console.debug(`${FN_NAME} > disabled > skipping comparison`);
    return;
  }

  const { aimedWithTraj, aimedWithShots } = config;

  console.debug(`${FN_NAME} > triggered`, {
    aimedWithTraj: {
      tilt: aimedWithTraj.ms.tilt,
      yaw: aimedWithTraj.ms.yaw,
    },
    aimedWithShots: {
      tilt: aimedWithShots.ms.tilt,
      yaw: aimedWithShots.ms.yaw,
    },
  });

  const tiltDelta = aimedWithTraj.ms.tilt - aimedWithShots.ms.tilt;
  console.assert(
    Math.abs(tiltDelta) < 0.01,
    `${FN_NAME} > tilt delta between aim w/ traj (${aimedWithTraj.ms.tilt}) vs aim w/ shots (${aimedWithShots.ms.tilt}) is too large: ${tiltDelta}`
  );

  const yawDelta = aimedWithTraj.ms.yaw - aimedWithShots.ms.yaw;
  console.assert(
    Math.abs(yawDelta) < 0.01,
    `${FN_NAME} > yaw delta between aim w/ traj (${aimedWithTraj.ms.yaw}) vs aim w/ shots (${aimedWithShots.ms.yaw}) is too large: ${yawDelta}`
  );
};

interface CommonSlice {
  lastChanged: number;

  isTraining: boolean;

  // only used in firing heartbeat during Test Shot step of install wizard, since we don't need to wait for training data
  ignoreTR?: boolean;

  requiresSend: boolean;

  // while in the future, a send to the machine is in flight
  sendingUntil: Date;

  // set this to slightly in the future when sending to machine so fire button immediately shows loading until after
  loadingUntil: Date;

  // e.g. auto-fire toggle automatically updates last MS hash, aiming should not require a resend
  readonly setSentHash: (value: string | undefined) => void;
}

interface BPSlice {
  pitchBP: IPitch | undefined;
  readonly setAndSendForBP: (config: ISetPitchBP) => Promise<void>;

  // updated whenever setAndSendForBP is triggered
  lastBsHash: string | undefined;
  // updated whenever setAndSendForBP is triggered
  lastSendConfig: ISendBP | undefined;

  // for plate view to reference
  plateBP: IPlateLoc;
  readonly setPlateBP: (loc: IPlateLoc) => void;
}

interface TrainingSlice {
  pitchTraining: ITrainingPitch | undefined;
  readonly setAndSendForTraining: (config: ISetPitchTraining) => Promise<void>;
  // uses the context pitch, DEFAULT_PLATE, and whatever shots are provided
  readonly aimForTraining: (
    config: IAimTrainingRequest
  ) => IAimedMsMsgResult | undefined;

  // for manual training input
  manualPlate: IPlateLoc;
  readonly setManualPlate: (loc: IPlateLoc) => void;
}

// MARK: getNextPlate
const getNextPlate = (config: {
  mode: ResetPlateMode;
  pitch: IPitch;
  strikeZone: IStrikeZone;
  fallback: IPlateLoc;
}): IPlateLoc => {
  if (config.pitch.debugMs) {
    // ? debug pitches always point at the default plate
    return clone(DEFAULT_PLATE);
  }

  const trajPlate = TrajHelper.getPlateLoc(config.pitch.traj);

  switch (config.mode) {
    case ResetPlateMode.Default: {
      return clone(DEFAULT_PLATE);
    }

    case ResetPlateMode.PitchBackup: {
      // what is the saved plate location?
      const targetPlate = config.pitch.plate_loc_backup ?? trajPlate;

      if (!USE_ABS_PITCH_BACKUP) {
        return targetPlate;
      }

      if (isNil(config.strikeZone)) {
        // skip rescale when height is not a number
        return targetPlate;
      }

      // how tall is the default SZ?
      const defaultSzHeightFt =
        VISIBLE_STRIKEZONE.top_ft - VISIBLE_STRIKEZONE.bottom_ft;

      // how far from the bottom of the SZ is the saved z value?
      const targetFromBottomFt =
        targetPlate.plate_z - VISIBLE_STRIKEZONE.bottom_ft;

      // get value as % between top and bottom
      const targetFromBottomPerc = targetFromBottomFt / defaultSzHeightFt;

      // how tall is the active SZ?
      const absSzHeightFt =
        config.strikeZone.top_ft - config.strikeZone.bottom_ft;

      const scaledFromBottomFt = targetFromBottomPerc * absSzHeightFt;

      // console.debug('getNextPlate', {
      //   defaultSzHeightFt,
      //   targetFromBottomFt,
      //   targetFromBottomPerc,
      //   absSzHeightFt,
      //   scaledFromBottomFt,
      //   plate_z: scaledFromBottomFt + config.strikeZone.bottom_ft,
      // });

      return {
        // * NOTE: horizontal doesn't need scaling since the width of the SZ never changes
        plate_x: targetPlate.plate_x,
        plate_z: scaledFromBottomFt + config.strikeZone.bottom_ft,
      };
    }

    case ResetPlateMode.PitchTraj: {
      return trajPlate;
    }

    default: {
      return config.fallback;
    }
  }
};

export type IAimingContext = CommonSlice & BPSlice & TrainingSlice;

// MARK: DEFAULT
const DEFAULT: IAimingContext = {
  lastChanged: Date.now(),

  isTraining: false,

  ignoreTR: false,

  pitchBP: undefined,
  setAndSendForBP: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  lastBsHash: undefined,
  lastSendConfig: undefined,

  pitchTraining: undefined,
  setAndSendForTraining: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  aimForTraining: () => undefined,

  plateBP: clone(DEFAULT_PLATE),
  setPlateBP: () => console.error(`${CONTEXT_NAME}: not init`),

  manualPlate: clone(DEFAULT_PLATE),
  setManualPlate: () => console.error(`${CONTEXT_NAME}: not init`),

  requiresSend: true,

  sendingUntil: new Date(),

  loadingUntil: new Date(),

  setSentHash: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const AimingContext = createContext(DEFAULT);

interface IProps extends IIgnoreAutoFireProp {
  // prevents accidental usage of BP vs Training fns
  training: boolean;

  // only used in firing heartbeat during Test Shot step of install wizard, since we don't need to wait for training data
  ignoreTR?: boolean;

  // when provided, every shot update query will include the value for newerThan (e.g. when training)
  newerThan?: string;

  children: ReactNode;
}

// MARK: AimingProvider
// automatically provides a firing context
export const AimingProvider: FC<IProps & IVerboseComponent> = (props) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  const current = useAuthStore((s) => s.current);

  // whether to use contAimingTraj instead of aimingTraj for aiming during BP
  const readyToContTrain = useTrainedMsStore((s) => s.readyToContTrain);

  const deliveryDict = useMiscStore((s) => s.deliveryDict);

  const {
    lastMSHash,
    machine,
    activeModel,
    activeStrikeZone,
    checkConfidence,
    sendPitchPreview,
    sendTarget,
  } = useContext(MachineContext);

  const latestSendPitchPreview = useLatest(sendPitchPreview);

  const latestSendTarget = useLatest(sendTarget);
  const latestDeliveryDict = useLatest(deliveryDict);

  const activeList = useListLibraryStore((s) => s.activeList);

  const activeHitter = useHittersStore((s) => s.activeHitter);

  const [pitchBP, setPitchBP] = useState(DEFAULT.pitchBP);

  const [plateBP, setPlateBP] = useState<IPlateLoc>(DEFAULT.plateBP);
  const latestPlateBP = useLatest(plateBP);

  const [pitchTraining, setPitchTraining] = useState(DEFAULT.pitchTraining);
  const latestPitchTraining = useLatest(pitchTraining);

  const [manualPlate, setManualPlate] = useState<IPlateLoc>(
    DEFAULT.manualPlate
  );

  const [sentHash, setSentHash] = useState<string | undefined>();

  const lastChangedSentHash = useMemo(() => Date.now(), [sentHash]);

  const [loadingUntil, setLoadingUntil] = useState(DEFAULT.loadingUntil);

  const [sendingUntil, setSendingUntil] = useState(DEFAULT.sendingUntil);

  // for determining whether a resend is necessary during CT
  const [lastBsHash, setLastBsHash] = useState<string>();
  // for resending w/ exactly the same details (apart from a new TMS during CT)
  const [lastSendConfig, setLastSendConfig] = useState<ISendBP>();

  // MARK: getBpVideoID
  const getBpVideoID = useCallback(
    async (pitch: IPitch | ITrainingPitch) => {
      const delivery = deliveryDict[pitch._id];

      const fromPitch = VideoHelper.getPitchDeliveryVideoID(pitch, delivery);

      if (fromPitch) {
        return fromPitch;
      }

      // todo: after converting lists context to zustand, replace this w/ a lazy load call to avoid reloading lists constantly
      const safeList =
        activeList?._id === pitch._parent_id
          ? activeList // the active list matches the pitch
          : await PitchListsService.getInstance().getOne(pitch._parent_id); // load the pitch's list

      const { suggestions } = useVideosStore.getState().getVideoOptions({
        releasePx: pitch.bs.px,
        suggestions: {
          list: safeList,
          pitch: pitch,
          hitter: activeHitter,
          delivery: delivery,
        },
      });

      const topSuggestion =
        !AUTO_USE_TOP_SUGGESTION || current.disable_auto_videos
          ? undefined
          : first(suggestions);

      if (topSuggestion?.value) {
        // value is the video's _id
        return topSuggestion.value;
      }

      const isLeft = BallHelper.getPitcherHand(pitch.bs.px) === PitcherHand.LHP;

      // note: default videos may get deleted
      const videoID = isLeft
        ? machine.default_video_lhp
        : machine.default_video_rhp;

      const defaultVideo = useVideosStore
        .getState()
        .videos.find((v) => v._id === videoID);

      if (defaultVideo) {
        // fallback to handle if the video doesn't exist anymore
        return defaultVideo._id;
      }

      // final fallback if nothing was found
      return isLeft ? StaticVideoType.default_LHP : StaticVideoType.default_RHP;
    },
    [
      props.training,
      deliveryDict,
      activeList,
      activeHitter,
      current.disable_auto_videos,
      machine.default_video_lhp,
      machine.default_video_rhp,
    ]
  );

  // MARK: packageAimedResult
  // shared by aimForBP and aimForTraining
  const packageAimedResult = useCallback(
    (
      config: IAimedMsMsgRequest & {
        aimed: IAimingOutputs;
        usingShots: IMachineShot[]; // provide an empty array when aiming w/ traj
        shotsRequired: boolean;
        videoID: string;
      }
    ) => {
      const { aimed } = config;

      const outputPitch = clone(config.pitch);
      outputPitch.bs = aimed.bs;
      outputPitch.traj = aimed.traj;

      const expectedTraj = aimed.aimingTraj
        ? TrajHelper.aimTrajectory(
            aimed.aimingTraj,
            {
              px: aimed.aimingTraj.px,
              py: aimed.aimingTraj.py,
              pz: aimed.aimingTraj.pz,
            },
            config.plate
          )
        : undefined;

      const msg: IMachineStateMsg = {
        ...aimed.ms,
        hash: null,
        force: null,
        jitter: null,
        ball_type: machine.ball_type,
        video_uuid: config.videoID,
        training: !!props.training,
        pitch_info: {
          type: outputPitch.type ?? null,
          target_traj: aimed.traj,
          expected_traj: expectedTraj ?? null,
          // So Vision knows not to bother processing this pitch.
          is_empty_carousel:
            outputPitch._id === TroubleshootingPitchID.EmptyCarousel,
        },
      };

      const output: IAimedMsMsgResult = {
        pitch: {
          ...outputPitch,
          _mx_ms: aimed.ms,
        },
        plate: config.plate,
        msg: msg,
        msgHash: MiscHelper.hashify(msg),
        usingShots: config.usingShots, // shots would only be relevant if we aimed w/ shots, not w/ traj
        usingTraj: aimed.usingTraj,
        aimingTraj: aimed.aimingTraj,
        shotsRequired: config.shotsRequired,
      };

      return output;
    },
    [
      machine.ball_type,
      getBpVideoID,
      props.training, // shouldn't ever change for the life of this context
    ]
  );

  // MARK: requiresSend
  const requiresSend = useMemo(
    () => !sentHash || sentHash !== lastMSHash,
    [sentHash, lastMSHash]
  );

  // MARK: aimForTraining
  const aimForTraining = useCallback(
    (config: IAimTrainingRequest) => {
      const FN_NAME = 'aimForTraining';

      if (!props.training) {
        NotifyHelper.error({
          message_md: `${props.identifier} cannot aim training pitch while configured for BP.`,
        });
        return;
      }

      const safePitch = config.pitch ?? latestPitchTraining.current;

      if (!safePitch) {
        return undefined;
      }

      const aimedWithShots = AimingHelper.aimWithShots({
        priority: safePitch.priority ?? BuildPriority.Spins,
        bs: safePitch.bs,
        traj: safePitch.traj,
        ms: safePitch._mx_ms,
        seams: safePitch.seams,
        breaks: safePitch.breaks,
        stepSize: undefined,
        releaseFt: machine.releaseFt,
        targetPlate: DEFAULT_PLATE,
        clampTilt: props.training,
        checkConfidence: checkConfidence,
        training: props.training,
        aimingShots: config.aimingShots,
      });

      if (!aimedWithShots) {
        verboseDebug(props, `${FN_NAME} > failed to aimWithShots`);
        return undefined;
      }

      const output = packageAimedResult({
        releaseFt: machine.releaseFt,
        pitch: safePitch,
        plate: DEFAULT_PLATE,
        aimed: aimedWithShots,
        aimingShots: config.aimingShots,
        usingShots: aimedWithShots.usingShots,
        aimingTraj: aimedWithShots.aimingTraj,
        shotsRequired: true,
        videoID: StaticVideoType.training_short,
      });

      return output;
    },
    [props.training, machine.releaseFt, checkConfidence, packageAimedResult]
  );

  const latestAimForTraining = useLatest(aimForTraining);

  const { releaseFt } = machine;

  // MARK: setAndSendForBP
  // * always invoke via setAndSendForBP so that sendingUntil is maintained
  const runSetAndSendForBP = useCallback(
    async (config: ISetPitchBP) => {
      const FN_NAME = 'setAndSendForBP';

      // MARK: setAndSendForBP > aimForBP
      const aimForBP = async (pitch: IPitch, plate: IPlateLoc) => {
        const FN_NAME = 'aimForBP';

        if (pitch.debugMs) {
          // when aiming a debug pitch, use the debug MS directly and skip all the aiming logic
          const output = packageAimedResult({
            releaseFt: releaseFt,
            pitch: pitch,
            plate: plate,
            aimed: {
              bs: pitch.bs,
              traj: pitch.traj,
              ms: pitch.debugMs,
              usingTraj: pitch.traj,
              aimingTraj: pitch.traj,
            },
            usingShots: [],
            aimingShots: [],
            aimingTraj: pitch.traj,
            shotsRequired: false,
            videoID: await getBpVideoID(pitch),
          });

          return output;
        }

        const storeTms =
          // use the override value if provided (e.g. during CT, to avoid staleness)
          config.sendConfig?.overrideTms ??
          // skip the mixin to ensure we always use the latest TMS from the store
          useTrainedMsStore.getState().getTms(pitch.bs_hash);

        if (!storeTms) {
          console.warn(`${FN_NAME} > cannot proceed without a TMS`);
          return undefined;
        }

        // ? in case the pitch's mixin value is stale and it's used in the rest of this flow
        pitch._mx_tms = storeTms;

        const { aimingTraj: safeAimingTraj, ms: safeMs } =
          AimingHelper.getAimingParams(storeTms, readyToContTrain);

        const aimedWithTraj =
          PREFER_AIM_WITH_TRAJ_FOR_BP && safeAimingTraj
            ? AimingHelper.aimWithTraj({
                priority: pitch.priority ?? BuildPriority.Spins,
                bs: pitch.bs,
                traj: pitch.traj,
                ms: safeMs,
                seams: pitch.seams,
                breaks: pitch.breaks,
                stepSize: undefined,
                releaseFt: releaseFt,
                targetPlate: plate,
                aimingTraj: safeAimingTraj,
              })
            : undefined;

        // load shots if:
        // - we are comparing aiming w/ shots vs w/ traj, or
        // - aiming w/ traj was not possible (e.g. no safeAimingTraj)
        // - aiming w/ traj failed
        // - aiming w/ traj is not enabled
        const shotsRequired =
          COMPARE_AIM_WITH_TRAJ_AND_SHOTS_FOR_BP || !aimedWithTraj;

        const aimingShots = shotsRequired
          ? await useTrainedMsStore.getState().lazyLoadShots(pitch.bs_hash)
          : [];

        // when loading shots was expected but nothing was found
        if (shotsRequired && aimingShots.length === 0) {
          console.warn(
            `${FN_NAME} > shots required but no shots could be loaded`
          );
          return undefined;
        }

        const aimedWithShots =
          !COMPARE_AIM_WITH_TRAJ_AND_SHOTS_FOR_BP && aimedWithTraj
            ? // if aiming w/ traj was successful and we're not comparing, don't bother aiming w/ shots
              undefined
            : AimingHelper.aimWithShots({
                priority: pitch.priority ?? BuildPriority.Spins,
                bs: pitch.bs,
                traj: pitch.traj,
                ms: safeMs,
                seams: pitch.seams,
                breaks: pitch.breaks,
                stepSize: undefined,
                releaseFt: releaseFt,
                targetPlate: plate,
                clampTilt: props.training,
                checkConfidence: false, // never check confidence for BP aiming w/ shots
                training: props.training,
                aimingShots: aimingShots,
              });

        if (
          COMPARE_AIM_WITH_TRAJ_AND_SHOTS_FOR_BP &&
          aimedWithTraj &&
          aimedWithShots
        ) {
          compareAiming({
            aimedWithTraj,
            aimedWithShots,
          });
        }

        const aimed = aimedWithTraj ?? aimedWithShots;

        if (!aimed) {
          verboseDebug(props, `${FN_NAME} > failed to aim`);
          return undefined;
        }

        const output = packageAimedResult({
          releaseFt: releaseFt,
          pitch: pitch,
          plate: plate,
          aimed: aimed,
          usingShots: aimedWithShots?.usingShots ?? [],
          aimingShots: aimingShots,
          aimingTraj: aimed.aimingTraj,
          shotsRequired: shotsRequired,
          videoID: await getBpVideoID(pitch),
        });

        // TMS has a null aimingTraj (e.g. from pre-6.7.0.1), replace it with the correctly calculated value
        const tryFixAimingTraj = (outputMs: IMachineState) => {
          // * IMPORTANT: only based on shotIDs (i.e. what's loaded in tmsShots)
          const aimingTraj = AimingHelper.getAimingTraj({
            refMs: safeMs,
            shots: aimingShots,
            releaseFt: releaseFt,
          });

          if (!aimingTraj) {
            console.warn(
              `${FN_NAME} > backfill aimingTraj > empty aimingTraj > no change`
            );
            return;
          }

          // test whether the aimingTraj gives 100% the same tilt and yaw as the output ms

          const testResult = AimingHelper.aimWithTraj({
            priority: pitch.priority ?? BuildPriority.Spins,
            bs: pitch.bs,
            traj: pitch.traj,
            ms: safeMs,
            seams: pitch.seams,
            breaks: pitch.breaks,
            stepSize: undefined,
            releaseFt: releaseFt,
            targetPlate: plate,
            aimingTraj: aimingTraj,
          });

          if (!testResult) {
            console.warn(
              `${FN_NAME} > backfill aimingTraj > failed to test result > no change`
            );
            return;
          }

          const tiltDelta = testResult.ms.tilt - outputMs.tilt;

          if (tiltDelta !== 0) {
            console.warn(
              `${FN_NAME} > backfill aimingTraj > non-zero tilt delta (${tiltDelta}) > no change`
            );
            return;
          }

          const yawDelta = testResult.ms.yaw - outputMs.yaw;

          if (yawDelta !== 0) {
            console.warn(
              `${FN_NAME} > backfill aimingTraj > non-zero yaw delta (${yawDelta}) > no change`
            );
            return;
          }

          console.debug(`${FN_NAME} > backfill aimingTraj > upserting TMS`, {
            aimingTraj,
          });

          useTrainedMsStore.getState().upsertTms({
            // unique fields for upsert filter
            machineID: storeTms.machineID,
            ball_type: storeTms.ball_type,
            bs_hash: storeTms.bs_hash,

            aimingTraj: aimingTraj,
            deleted: !!storeTms.deleted, // keep the same deleted value as the current TMS record
          });
        };

        if (storeTms._id && !storeTms.aimingTraj && output.msg) {
          tryFixAimingTraj(output.msg);
        }

        return output;
      };

      if (props.training) {
        NotifyHelper.error({
          message_md: `${props.identifier} cannot set BP pitch while configured for training.`,
        });
        return;
      }

      // forces resend to machine
      setSentHash(undefined);
      setPitchBP(config.pitch);

      setLastBsHash(config.pitch?.bs_hash);
      setLastSendConfig(config.sendConfig);

      // follow-up actions are only relevant for actual pitches
      if (!config.pitch) {
        return;
      }

      useMiscStore
        .getState()
        .setDelivery(
          config.pitch._id,
          latestDeliveryDict.current[config.pitch._id] ?? DEFAULT_DELIVERY
        );

      const nextPlate = config.resetPlate
        ? getNextPlate({
            mode: config.resetPlate,
            pitch: config.pitch,
            strikeZone: activeStrikeZone,
            fallback: latestPlateBP.current,
          })
        : latestPlateBP.current;

      if (config.resetPlate) {
        verboseDebug(props, `${FN_NAME} > reset plate`, nextPlate);

        // i.e. don't do anything when resetPlate mode is not specified
        setPlateBP(nextPlate);
      }

      if (!config.sendConfig) {
        setSentHash(undefined);
        return;
      }

      const { hitter, list, skipFiringCheck, skipPreview, trigger, onSuccess } =
        config.sendConfig;

      // * FOR SAFETY - never allow sending a non-training pitch to machine unless there is "sufficient" training data
      if (
        !onDemand &&
        !config.pitch._mx_tms?._mx_trained &&
        !config.pitch._mx_tms?.debug // skip this check for debug pitches
      ) {
        NotifyHelper.warning({
          message_md: 'Please retrain the pitch and try again.',
        });
        setSentHash(undefined);
        return;
      }

      const aimed = await aimForBP(config.pitch, nextPlate);

      if (!aimed) {
        verboseDebug(props, `${FN_NAME} > no aimed result`);
        setPitchBP(undefined);
        return;
      }

      if (!aimed.msg) {
        verboseDebug(props, `${FN_NAME} > aimed result > no msg`);
        setPitchBP(undefined);
        return;
      }

      if (!skipPreview) {
        latestSendPitchPreview.current({
          trigger: trigger,
          current: aimed.pitch,
        });
      }

      if (
        aimed.shotsRequired && // shots were required (e.g. aiming w/ traj was not possible)
        aimed.usingShots.length === 0 // no shots were used
      ) {
        // ? if the shots were deleted or the request failed (e.g. network issue)
        NotifyHelper.warning({
          message_md:
            'Failed to load training data. Please check your network connection and/or retrain the pitch.',
        });

        verboseDebug(props, `${FN_NAME} > aimed result > no shots`);
        setPitchBP(undefined);
        return;
      }

      verboseDebug(props, `${FN_NAME} > send`, aimed);

      const result = await latestSendTarget.current({
        source: `${props.identifier} sendTarget > ${aimed.usingShots.length} shots`,

        // from aimed
        msMsg: aimed.msg,
        pitch: aimed.pitch,
        plate: aimed.plate,

        ref_py: config.pitch._mx_tms?.ms.py ?? 0,

        // misc metadata
        video: useVideosStore
          .getState()
          .videos.find((v) => v._id === aimed.msg?.video_uuid),
        list: list,
        hitter_id: hitter?._id,
        trigger: trigger,
        skipFiringCheck: skipFiringCheck,
      });

      if (result.success) {
        setLoadingUntil(addMilliseconds(new Date(), 500));
        setSentHash(result.hash);
      }

      onSuccess?.(result.success);
    },
    [
      onDemand,
      releaseFt,
      activeModel,
      activeStrikeZone,
      props.training,
      props.identifier,
      readyToContTrain, // ? whether the machine's current tracking device is CT-supported and the user has enabled CT
      packageAimedResult,
    ]
  );

  const setAndSendForBP = useCallback(
    async (config: ISetPitchBP) => {
      // * must land in the same React batch as the setSentHash(undefined) below, so the heartbeat
      // * never sees requiresSend without also seeing the send in flight
      setSendingUntil(addSeconds(new Date(), SEND_TIMEOUT_SEC));

      try {
        await runSetAndSendForBP(config);
      } finally {
        setSendingUntil(new Date());
      }
    },
    [runSetAndSendForBP]
  );

  // MARK: setAndSendForTraining
  // * always invoke via setAndSendForTraining so that sendingUntil is maintained
  const runSetAndSendForTraining = useCallback(
    async (config: ISetPitchTraining) => {
      const FN_NAME = 'setAndSendForTraining';

      if (!props.training) {
        NotifyHelper.error({
          message_md: `${props.identifier} cannot set training pitch while configured for BP.`,
        });
        return;
      }

      // forces resend to machine
      setSentHash(undefined);
      setPitchTraining(config.pitch);

      setLastBsHash(config.pitch?.bs_hash);
      setLastSendConfig(config.sendConfig);

      // follow-up actions are only relevant for actual pitches
      if (!config.pitch) {
        return;
      }

      setPlateBP(DEFAULT_PLATE);

      if (!config.sendConfig) {
        setSentHash(undefined);
        return;
      }

      // aim for training
      const {
        aiming,
        collectionID,
        skipFiringCheck,
        skipPreview,
        stepSize,
        trigger,
        aimingShots,
        onSuccess,
      } = config.sendConfig;

      const aimed = await latestAimForTraining.current({
        pitch: config.pitch,
        aimingShots: aimingShots,
        stepSize: stepSize,
      });

      if (!aimed) {
        verboseDebug(props, `${FN_NAME} > aimTraining > no result`);
        setPitchTraining(undefined);
        return;
      }

      if (!aimed.msg) {
        verboseDebug(props, `${FN_NAME} > aimTraining > no msg`);
        setPitchTraining(undefined);
        return;
      }

      if (!skipPreview) {
        latestSendPitchPreview.current({
          trigger: trigger,
          current: aimed.pitch,
        });
      }

      verboseDebug(props, `${FN_NAME} > send`, aimed);

      const result = await latestSendTarget.current({
        source: `${props.identifier} > sendTarget > ${aimed.usingShots.length} shots`,
        msMsg: aimed.msg,
        pitch: aimed.pitch,
        video: undefined,
        plate: DEFAULT_PLATE,
        ref_py: config.pitch._mx_ms.py,
        collectionID: collectionID,
        aiming: aiming,
        trigger: trigger,
        skipFiringCheck: skipFiringCheck,
      });

      if (result.success) {
        setLoadingUntil(addMilliseconds(new Date(), 500));
        setSentHash(result.hash);
      }

      onSuccess?.(result.success);
    },
    [props.identifier, props.verbose, props.training]
  );

  const setAndSendForTraining = useCallback(
    async (config: ISetPitchTraining) => {
      // * see setAndSendForBP re: batching
      setSendingUntil(addSeconds(new Date(), SEND_TIMEOUT_SEC));

      try {
        await runSetAndSendForTraining(config);
      } finally {
        setSendingUntil(new Date());
      }
    },
    [runSetAndSendForTraining]
  );

  // MARK: state
  const state: IAimingContext = {
    lastChanged: lastChangedSentHash,

    isTraining: props.training,

    pitchBP: props.training ? undefined : pitchBP,

    ignoreTR: props.ignoreTR,

    setAndSendForBP: setAndSendForBP,

    lastBsHash: lastBsHash,
    lastSendConfig: lastSendConfig,

    plateBP: props.training ? DEFAULT_PLATE : plateBP,

    // todo: turn this into a callback
    setPlateBP: (loc) => {
      if (props.training) {
        NotifyHelper.error({
          message_md: `${props.identifier} cannot set BP plate while configured for training.`,
        });
        return;
      }

      // forces resend to machine
      setSentHash(undefined);
      setPlateBP(loc);
    },

    manualPlate: manualPlate,
    setManualPlate: setManualPlate,

    pitchTraining: props.training ? pitchTraining : undefined,

    setAndSendForTraining: setAndSendForTraining,

    aimForTraining: aimForTraining,

    requiresSend: requiresSend,

    sendingUntil: sendingUntil,

    loadingUntil: loadingUntil,

    setSentHash: setSentHash,
  };

  // MARK: AimingProvider > return
  return (
    <AimingContext.Provider value={state}>
      <FiringProvider
        identifier={props.identifier + 'FiringCx'}
        ignoreAfOnTr={props.ignoreAfOnTr}
        verbose={props.verbose}
      >
        {props.children}
      </FiringProvider>
    </AimingContext.Provider>
  );
};
